import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { Plus, X } from "lucide-react";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import type { InternalUser } from "@/features/tasks/types";
import { getRoleLabel } from "@/utils/roleLabel";

// Shared visual style — must match between overlay div and textarea exactly
const SHARED_STYLE: React.CSSProperties = {
  fontSize: "0.875rem",
  lineHeight: "1.5rem",
  padding: "0.5rem 0.75rem",
  fontFamily: "inherit",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
  boxSizing: "border-box",
};

interface Props {
  value: string;
  mentionIds: string[];
  onChange: (value: string, mentionIds: string[]) => void;
  onSubmit?: () => void;
  onFilePaste?: (files: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  className?: string;
  /**
   * When true, contractor users with has_friday_access=True are also offered
   * in the mention dropdown. Set to true in every Friday (tasks) usage.
   */
  includeFridayContractors?: boolean;
}

export interface MentionTextareaHandle {
  focus(): void;
}

const DROPDOWN_MAX_H = 212;

interface DropdownPos { top: number; left: number; width: number; maxH: number; }

export const MentionTextarea = forwardRef<MentionTextareaHandle, Props>(function MentionTextarea({
  value,
  mentionIds,
  onChange,
  onSubmit,
  onFilePaste,
  disabled,
  placeholder = "Napisz komentarz… (wpisz @ aby oznaczyć)",
  rows = 2,
  className = "",
  includeFridayContractors = false,
}, ref) {
  const { data: rawUsers = [] } = useInternalUsers({ includeFridayContractors });
  const allUsers = [...rawUsers].sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"));

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const [dropdown, setDropdown] = useState<InternalUser[]>([]);
  const [dropPos, setDropPos] = useState<DropdownPos | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addDropPos, setAddDropPos] = useState<DropdownPos | null>(null);

  useImperativeHandle(ref, () => ({
    focus() { textareaRef.current?.focus(); },
  }));

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 300)}px`;
  }, []);

  useEffect(() => { autoResize(); }, [value, autoResize]);

  const mentionedNames = mentionIds
    .map((id) => allUsers.find((u) => u.id === id)?.full_name)
    .filter((n): n is string => !!n);

  function calcDropPos(rect: DOMRect): DropdownPos {
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    if (spaceBelow >= 80 || spaceBelow >= spaceAbove) {
      const maxH = Math.min(DROPDOWN_MAX_H, Math.max(80, spaceBelow));
      return { top: rect.bottom + 4, left: rect.left, width: rect.width, maxH };
    } else {
      const maxH = Math.min(DROPDOWN_MAX_H, Math.max(80, spaceAbove));
      return { top: rect.top - maxH - 4, left: rect.left, width: rect.width, maxH };
    }
  }

  useEffect(() => {
    if (!dropdown.length) return;
    function update() {
      const rect = textareaRef.current?.getBoundingClientRect();
      if (rect) setDropPos(calcDropPos(rect));
    }
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [dropdown.length]);

  function openDropdown(filtered: InternalUser[]) {
    const rect = textareaRef.current?.getBoundingClientRect();
    if (rect) setDropPos(calcDropPos(rect));
    setDropdown(filtered);
  }

  function closeDropdown() {
    setDropdown([]);
    setDropPos(null);
  }

  function handleChange(raw: string, cursor: number) {
    const match = raw.slice(0, cursor).match(/@([^\s@]*)$/);
    if (match) {
      const q = match[1].toLowerCase();
      openDropdown(
        allUsers
          .filter((u) => u.full_name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))
          .slice(0, 8)
      );
    } else {
      closeDropdown();
    }
    onChange(raw, mentionIds);
  }

  function selectMention(user: InternalUser) {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart;
    const before = value.slice(0, cursor).replace(/@([^\s@]*)$/, `@${user.full_name} `);
    const after = value.slice(cursor);
    const newIds = mentionIds.includes(user.id) ? mentionIds : [...mentionIds, user.id];
    onChange(before + after, newIds);
    closeDropdown();
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(before.length, before.length);
    }, 0);
  }

  function removeMention(id: string) {
    onChange(value, mentionIds.filter((m) => m !== id));
  }

  // ── "Dodaj" button picker ──────────────────────────────────────────────────

  function openAddPicker() {
    const rect = addBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    let pos: DropdownPos;
    if (spaceBelow >= 80 || spaceBelow >= spaceAbove) {
      pos = { top: rect.bottom + 4, left: rect.left, width: 220, maxH: Math.min(DROPDOWN_MAX_H, Math.max(80, spaceBelow)) };
    } else {
      const maxH = Math.min(DROPDOWN_MAX_H, Math.max(80, spaceAbove));
      pos = { top: rect.top - maxH - 4, left: rect.left, width: 220, maxH };
    }
    setAddDropPos(pos);
    setAddSearch("");
    setAddOpen(true);
  }

  function selectAddUser(user: InternalUser) {
    const newIds = mentionIds.includes(user.id) ? mentionIds : [...mentionIds, user.id];
    const mention = `@${user.full_name} `;
    onChange(value + mention, newIds);
    setAddOpen(false);
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    }, 0);
  }

  const addFiltered = allUsers
    .filter((u) => !mentionIds.includes(u.id))
    .filter((u) =>
      !addSearch ||
      u.full_name.toLowerCase().includes(addSearch.toLowerCase()) ||
      u.username.toLowerCase().includes(addSearch.toLowerCase())
    );

  return (
    <div className="flex flex-col gap-1.5">
      {/* Wrapper — textarea stacked over highlight overlay */}
      <div className={`relative focus-within:border-[var(--color-primary)] ${disabled ? "opacity-50" : ""}`}>

        {/* Highlight overlay */}
        <div
          ref={overlayRef}
          aria-hidden
          style={{ ...SHARED_STYLE, pointerEvents: "none", position: "absolute", inset: 0, overflow: "hidden", color: "transparent" }}
        >
          {buildOverlay(value, mentionedNames)}
          &#8203;
        </div>

        {/* Actual textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          rows={rows}
          disabled={disabled}
          placeholder={placeholder}
          style={{ ...SHARED_STYLE, caretColor: "#111827", color: "transparent", background: "transparent", position: "relative", display: "block", overflowY: "auto", maxHeight: "300px" }}
          className={`w-full resize-none outline-none placeholder:text-gray-400 placeholder:opacity-100 ${className}`}
          onChange={(e) => handleChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
          onScroll={(e) => {
            if (overlayRef.current) overlayRef.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop;
          }}
          onKeyDown={(e) => {
            if (dropdown.length > 0 && (e.key === "Enter" || e.key === "Tab")) {
              e.preventDefault();
              selectMention(dropdown[0]);
              return;
            }
            if (e.key === "Escape") { closeDropdown(); return; }
            if (onSubmit && e.key === "Enter" && !e.shiftKey && dropdown.length === 0) {
              e.preventDefault();
              onSubmit();
            }
            if (onSubmit && e.key === "Enter" && e.ctrlKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          onBlur={() => setTimeout(closeDropdown, 150)}
          onPaste={(e) => {
            if (!onFilePaste) return;
            const files = Array.from(e.clipboardData.items)
              .filter((item) => item.kind === "file")
              .map((item) => item.getAsFile())
              .filter((f): f is File => f !== null);
            if (files.length > 0) {
              e.preventDefault();
              onFilePaste(files);
            }
          }}
        />
      </div>

      {/* Chips row: existing mentions + "Dodaj" button */}
      <div className="flex gap-1.5 flex-wrap items-center min-h-[1.5rem]">
        {mentionIds.map((id) => {
          const u = allUsers.find((x) => x.id === id);
          return u ? (
            <button
              key={id}
              type="button"
              onClick={() => removeMention(id)}
              className="text-xs font-mono bg-[var(--color-primary-dim)] text-[var(--color-primary)] px-2 py-0.5 border border-[var(--color-primary)] hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-1"
              title="Kliknij, aby usunąć"
            >
              @{u.full_name} <X size={10} />
            </button>
          ) : null;
        })}
        <button
          ref={addBtnRef}
          type="button"
          onClick={openAddPicker}
          className="text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] flex items-center gap-0.5 transition-colors"
          title="Dodaj oznaczenie"
        >
          <Plus size={11} />
          Dodaj
        </button>
      </div>

      {/* @ typing dropdown */}
      {dropdown.length > 0 && dropPos && createPortal(
        <div
          style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: Math.max(dropPos.width, 220), maxHeight: dropPos.maxH, zIndex: 9999 }}
          className="bg-white overflow-y-auto"
        >
          {dropdown.map((u) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectMention(u); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-primary-dim)] flex items-center gap-2"
            >
              <span className="w-6 h-6 rounded-full bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)] text-xs font-bold flex items-center justify-center flex-shrink-0">
                {u.full_name.charAt(0)}
              </span>
              <div>
                <p className="font-medium text-gray-800">{u.full_name}</p>
                <p className="text-xs text-gray-400">{getRoleLabel(u.role, u.is_ceo)}</p>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* "Dodaj" picker dropdown */}
      {addOpen && addDropPos && createPortal(
        <div
          style={{ position: "fixed", top: addDropPos.top, left: addDropPos.left, width: addDropPos.width, maxHeight: addDropPos.maxH, zIndex: 9999 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl flex flex-col overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input
              autoFocus
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              placeholder="Szukaj osoby…"
              className="w-full text-xs px-2 py-1.5 rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder:text-gray-400"
              onKeyDown={(e) => {
                if (e.key === "Escape") setAddOpen(false);
                if (e.key === "Enter" && addFiltered.length > 0) selectAddUser(addFiltered[0]);
              }}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {addFiltered.length === 0 ? (
              <p className="text-xs text-gray-400 px-3 py-2">Brak wyników</p>
            ) : addFiltered.map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); selectAddUser(u); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/20 flex items-center gap-2"
              >
                <span className="w-6 h-6 rounded-full bg-[var(--color-primary-dim)] text-[var(--color-primary)] text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {u.full_name.charAt(0)}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{u.full_name}</p>
                  <p className="text-xs text-gray-400">{getRoleLabel(u.role, u.is_ceo)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

// ── Overlay builder ───────────────────────────────────────────────────────────

function buildOverlay(text: string, mentionedNames: string[]): React.ReactNode {
  if (!mentionedNames.length) {
    return <span style={{ color: "#111827" }}>{text}</span>;
  }
  const sorted = [...mentionedNames].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const regex = new RegExp(`(@(?:${escaped.join("|")}))`, "g");

  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(<span key={`t${last}`} style={{ color: "#111827" }}>{text.slice(last, m.index)}</span>);
    nodes.push(
      <mark key={`m${m.index}`} style={{ background: "#fee2e2", color: "#b91c1c", borderRadius: "2px" }}>
        {m[0]}
      </mark>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(<span key={`t${last}`} style={{ color: "#111827" }}>{text.slice(last)}</span>);
  return nodes;
}

// ── renderMentions — for displaying sent messages ─────────────────────────────

export function renderMentions(content: string, users?: { full_name: string }[]): React.ReactNode {
  const sorted = (users ?? []).sort((a, b) => b.full_name.length - a.full_name.length);

  if (sorted.length > 0) {
    const escaped = sorted.map((u) => u.full_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = new RegExp(`(@(?:${escaped.join("|")}|[^\\s]+))`, "g");
    const nodes: React.ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(content)) !== null) {
      if (m.index > last) nodes.push(content.slice(last, m.index));
      nodes.push(
        <span key={m.index} className="mention">
          {m[0]}
        </span>
      );
      last = m.index + m[0].length;
    }
    if (last < content.length) nodes.push(content.slice(last));
    return nodes.length ? nodes : content;
  }

  const parts = content.split(/(@[^\s]+)/g);
  return parts.map((part, i) =>
    part.startsWith("@")
      ? <span key={i} className="mention">{part}</span>
      : part
  );
}
